import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useClients, useAllMachines, usePaperTypes, useTopFinishes } from '../lib/lookups';
import { inchesToMm } from '../lib/units';

interface Product {
  id: string; name: string; client: string;
  artwork: string; dieNumber: string; plateNumber: string;
  noOfUps: string; productRate: string;
  paperType: string; sheetSize: string;
  sheetCutSizeLength: string; sheetCutSizeWidth: string;
  laminationType: string;
  productLength: string; productWidth: string; productHeight: string;
  foilingColor: string; filmSizeMicron: string; embossingNo: string;
  shadeCardNo: string; prePostLamination: string;
  onlineEmbossing: boolean; offlineEmbossing: boolean; braille: boolean;
  spotUv: boolean; screenUv: boolean; offlineCoating: boolean; onlineCoating?: boolean; baseCoat?: boolean;
  cartonType: string; cartonSizeL: string; cartonSizeW: string; cartonSizeH: string;
  machineFront: string; machineFrontColor: string;
  machineFront2: string; machineFront2Color: string;
  machineBack: string; machineBackColor: string;
  artWNo: string; remark: string;
  /** The GSM the party (client) asks for. */
  partyGsm?: string;
  orderCount: number;
  createdOn: string | null; updatedOn: string | null;
}

const yn = (b: boolean) => (b ? 'Yes' : 'No');
const dim = (v: string) => (v ? inchesToMm(v) : '');

export function ProductCardPrintPage() {
  const { id } = useParams();
  const q = useQuery({
    queryKey: ['product-print', id],
    queryFn: () => api<Product>(`/api/products/${id}`),
  });

  const clients = useClients();
  // The card names whatever machine the product carries, a removed one too.
  const machines = useAllMachines();
  const papers = usePaperTypes();
  const finishes = useTopFinishes();

  useEffect(() => {
    if (q.data) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [q.data]);

  if (q.isLoading) return <div className="p-6 text-slate-500">Loading product sheet…</div>;
  if (q.isError || !q.data) {
    return (
      <div className="p-6">
        <p className="text-red-600">Could not load product: {(q.error as Error)?.message ?? 'unknown'}</p>
        <Link to={listReturnUrl('/products')} className="text-brand underline text-sm">← Back to Product Master</Link>
      </div>
    );
  }
  const r = q.data;

  // Resolve IDs to names where possible (lookups may still be loading; fall back to raw id).
  const lookupName = (id: string, opts: { id: string; name: string }[] | undefined): string => {
    if (!id) return '';
    return opts?.find((o) => o.id === id)?.name ?? id;
  };

  const clientName = lookupName(r.client, clients.data);
  const paperName = lookupName(r.paperType, papers.data);
  const lamName = lookupName(r.laminationType, finishes.data);
  const machFront = lookupName(r.machineFront, machines.data);
  const machFront2 = lookupName(r.machineFront2, machines.data);
  const machBack = lookupName(r.machineBack, machines.data);

  const productDims = [dim(r.productLength), dim(r.productHeight), dim(r.productWidth)]
    .filter(Boolean).join(' × ');
  const cutSize = [dim(r.sheetCutSizeLength), dim(r.sheetCutSizeWidth)]
    .filter(Boolean).join(' × ');
  // "571.5 x 990.6 (290)": the board's GSM comes off - the sheet shows the party's GSM.
  const sheetDimsOnly = (r.sheetSize ?? '').replace(/\s*\(\d+(?:\.\d+)?\)\s*$/, '');

  return (
    <div className="p-6 print:p-0 max-w-[820px] mx-auto bg-white text-slate-900 text-[12px] print-page-container"
         style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <Link to={listReturnUrl('/products')} className="text-sm text-slate-600 hover:underline">← Back</Link>
        <button onClick={() => window.print()} className="btn-primary">Print / Save as PDF</button>
      </div>

      {/* Header */}
      <div className="border-2 border-slate-900 p-3 mb-2 text-center">
        <div className="text-lg font-bold tracking-wide">VASUDEV PRINT PACK PVT. LTD.</div>
        <div className="text-xs">Hansa Industrial Park, Derabassi-Barwala Rd, Dera Bassi, Punjab 140507</div>
        <div className="text-xs">Phone: 085807 03745 · mis@vasudevprintpack.com</div>
        <div className="mt-1 text-sm font-bold uppercase tracking-widest">Product Master Sheet</div>
      </div>

      {/* Product header */}
      <table className="w-full border border-slate-900 mb-2">
        <tbody>
          <tr>
            <Th>Product Code</Th><Td colSpan={3}>{r.id}</Td>
          </tr>
          <tr>
            <Th>Product Name</Th><Td colSpan={3}>{r.name}</Td>
          </tr>
          <tr>
            <Th>Client</Th><Td colSpan={3}>{clientName}</Td>
          </tr>
        </tbody>
      </table>

      {/* Basic */}
      <SectionHeader>Basic</SectionHeader>
      <table className="w-full border border-slate-900 mb-2">
        <tbody>
          <tr>
            <Th>Die No</Th><Td>{r.dieNumber}</Td>
            <Th>Design No (Artwork)</Th><Td>{r.artwork}</Td>
            <Th>A/W Material No</Th><Td>{r.artWNo}</Td>
          </tr>
          <tr>
            <Th>Plate Number</Th><Td>{r.plateNumber}</Td>
            <Th>No. of Ups</Th><Td colSpan={3}>{r.noOfUps}</Td>
          </tr>
        </tbody>
      </table>

      {/* Paper & Sheet */}
      <SectionHeader>Paper & Sheet</SectionHeader>
      <table className="w-full border border-slate-900 mb-2">
        <tbody>
          <tr>
            <Th>Paper Type</Th><Td>{paperName}</Td>
            <Th>Full Sheet Size (mm)</Th><Td>{sheetDimsOnly}</Td>
            <Th>Party GSM</Th><Td>{r.partyGsm ?? ''}</Td>
          </tr>
          <tr>
            <Th>Surface Finish Type</Th><Td>{lamName}</Td>
            <Th>Sheet Cut Size (mm)</Th><Td>{cutSize}</Td>
            <Th>Product Size L×H×W (mm)</Th><Td>{productDims}</Td>
          </tr>
        </tbody>
      </table>

      {/* Finishing */}
      <SectionHeader>Finishing & Specials</SectionHeader>
      <table className="w-full border border-slate-900 mb-2">
        <tbody>
          <tr>
            <Th>Foiling Color</Th><Td>{r.foilingColor}</Td>
            <Th>Film Size & Micron</Th><Td>{r.filmSizeMicron}</Td>
            <Th>Embossing No</Th><Td>{r.embossingNo}</Td>
          </tr>
          <tr>
            <Th>Shade Card No</Th><Td>{r.shadeCardNo}</Td>
            <Th>Pre / Post Lamination</Th><Td>{r.prePostLamination}</Td>
            <Th>Online Embossing</Th><Td>{yn(r.onlineEmbossing)}</Td>
          </tr>
          <tr>
            <Th>Offline Embossing</Th><Td>{yn(r.offlineEmbossing)}</Td>
            <Th>Braille</Th><Td>{yn(r.braille)}</Td>
            <Th>Spot UV</Th><Td>{yn(r.spotUv)}</Td>
          </tr>
          <tr>
            <Th>Screen UV</Th><Td>{yn(r.screenUv)}</Td>
            <Th>Offline Coating</Th><Td>{yn(r.offlineCoating)}</Td>
            <Th>Online Coating</Th><Td>{yn(Boolean(r.onlineCoating))}</Td>
          </tr>
          <tr>
            <Th>Base Coat</Th><Td>{yn(Boolean(r.baseCoat))}</Td>
            <Th>{' '}</Th><Td>{' '}</Td>
            <Th>{' '}</Th><Td>{' '}</Td>
          </tr>
        </tbody>
      </table>

      {/* Carton */}
      <SectionHeader>Carton</SectionHeader>
      <table className="w-full border border-slate-900 mb-2">
        <tbody>
          <tr>
            <Th>Carton Type</Th><Td colSpan={5}>{r.cartonType}</Td>
          </tr>
        </tbody>
      </table>

      {/* Machine */}
      <SectionHeader>Machine</SectionHeader>
      <table className="w-full border border-slate-900 mb-4">
        <tbody>
          <tr>
            <Th>Machine Front</Th><Td>{machFront}</Td>
            <Th>Front Color</Th><Td>{r.machineFrontColor}</Td>
            <Th>{' '}</Th><Td>{' '}</Td>
          </tr>
          <tr>
            <Th>Pass (2)</Th><Td>{machFront2}</Td>
            <Th>Pass (2) Color</Th><Td>{r.machineFront2Color}</Td>
            <Th>{' '}</Th><Td>{' '}</Td>
          </tr>
          <tr>
            <Th>Machine Back</Th><Td>{machBack}</Td>
            <Th>Back Color</Th><Td>{r.machineBackColor}</Td>
          </tr>
        </tbody>
      </table>

      {r.remark && (
        <>
          <SectionHeader>Remark</SectionHeader>
          <div className="border border-slate-900 p-2 mb-3 whitespace-pre-wrap">{r.remark}</div>
        </>
      )}

      <div className="text-[10px] text-slate-500 text-right print:mt-2">
        All dimensions in mm. Generated by Ab CRM v2 on {new Date().toLocaleString()}.
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <td className="border border-slate-900 bg-slate-100 px-2 py-1 font-semibold w-[14%] align-top">{children}</td>;
}
function Td({ children, colSpan }: { children: React.ReactNode; colSpan?: number }) {
  return <td className="border border-slate-900 px-2 py-1 align-top" colSpan={colSpan}>{children}</td>;
}
function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div className="bg-brand text-white text-xs font-semibold uppercase tracking-wider px-2 py-1 mt-2">{children}</div>;
}
